'use client';

import * as React from 'react';
import { Check, Globe, FileText, CheckCircle, SlidersHorizontal, LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

interface StepperContextValue {
  currentStep: number;
  goToStep: (step: number) => void;
  nextStep: () => void;
  previousStep: () => void;
  steps: Array<{ id: string; label: string }>;
  hasConditionalStep?: boolean;
}

const StepperContext = React.createContext<StepperContextValue | null>(null);

export const useStepperContext = () => {
  const context = React.useContext(StepperContext);
  if (!context) {
    throw new Error('useStepperContext must be used within StepperProvider');
  }
  return context;
};

interface StepperProviderProps {
  children: React.ReactNode;
  initialStep?: number;
  onStepChange?: (step: number) => void;
  hasConditionalStep?: boolean; // Pour indiquer si une étape conditionnelle existe (ex: limitsVariants pour Cash Game)
}

export const StepperProvider: React.FC<StepperProviderProps> = ({ 
  children, 
  initialStep = 0,
  onStepChange,
  hasConditionalStep = false
}) => {
  const [currentStep, setCurrentStep] = React.useState(initialStep);
  
  const steps = React.useMemo(() => [
    { id: 'format', label: 'Univers & Format' },
    { id: 'details', label: 'Détails' },
    ...(hasConditionalStep ? [{ id: 'limitsVariants', label: 'Limite et Variante' }] : []),
    { id: 'summary', label: 'Récapitulatif' }
  ], [hasConditionalStep]);

  // Synchroniser currentStep avec initialStep quand il change
  React.useEffect(() => {
    setCurrentStep(initialStep);
  }, [initialStep]);


  const goToStep = React.useCallback((step: number) => {
    setCurrentStep(step);
    onStepChange?.(step);
  }, [onStepChange]);

  const nextStep = React.useCallback(() => {
    setCurrentStep(prev => {
      const maxStep = steps.length - 1;
      const next = Math.min(prev + 1, maxStep);
      onStepChange?.(next);
      return next;
    });
  }, [onStepChange, steps.length]);

  const previousStep = React.useCallback(() => {
    setCurrentStep(prev => {
      const next = Math.max(prev - 1, 0);
      onStepChange?.(next);
      return next;
    });
  }, [onStepChange]);

  const value = {
    currentStep,
    goToStep,
    nextStep,
    previousStep,
    steps,
  };

  return (
    <StepperContext.Provider value={value}>
      {children}
    </StepperContext.Provider>
  );
};

// Mapping des icônes pour chaque étape
const stepIcons: Record<string, LucideIcon> = {
  format: Globe,
  limitsVariants: SlidersHorizontal,
  details: FileText,
  summary: CheckCircle,
};

interface StepperListProps {
  children: React.ReactNode;
  className?: string;
}

export const StepperList: React.FC<StepperListProps> = ({ children, className }) => {
  return (
    <div className={cn('flex items-center justify-between w-full relative py-6 px-2', className)}>
      {children}
    </div>
  );
};

interface StepperItemProps {
  stepId: string;
  children: React.ReactNode;
}

export const StepperItem: React.FC<StepperItemProps> = ({ stepId, children }) => {
  return (
    <div className="flex items-start relative flex-shrink-0">
      {children}
    </div>
  );
};

interface StepperTriggerProps {
  children: React.ReactNode;
  className?: string;
}

export const StepperTrigger: React.FC<StepperTriggerProps> = ({ children, className }) => {
  return (
    <div className={cn('flex flex-col items-center cursor-default', className)}>
      {children}
    </div>
  );
};

interface StepperIndicatorProps {
  stepId: string;
  className?: string;
}

export const StepperIndicator: React.FC<StepperIndicatorProps> = ({ stepId, className }) => {
  const { currentStep, steps } = useStepperContext();
  const stepIndex = steps.findIndex(s => s.id === stepId);
  
  const isCompleted = stepIndex < currentStep;
  const isActive = stepIndex === currentStep;
  const Icon = stepId ? stepIcons[stepId] : null;
  
  return (
    <div
      className={cn(
        'flex items-center justify-center w-10 h-10 rounded-full border-2 transition-all duration-200 relative z-10 flex-shrink-0',
        isCompleted || isActive
          ? 'bg-blue-600 border-white text-white shadow-md'
          : 'bg-gray-700 border-white text-white dark:bg-gray-700',
        isCompleted && 'animate-in fade-in zoom-in-95 duration-500',
        className
      )}
    >
      {isCompleted ? (
        <Check className="w-5 h-5 animate-in fade-in zoom-in-95 duration-500" strokeWidth={3} />
      ) : Icon ? (
        <Icon className="w-5 h-5" strokeWidth={2} />
      ) : null}
    </div>
  );
};

interface StepperTitleProps {
  children: React.ReactNode;
  stepId: string;
  className?: string;
}

export const StepperTitle: React.FC<StepperTitleProps> = ({ children, stepId, className }) => {
  const { currentStep, steps } = useStepperContext();
  const stepIndex = steps.findIndex(s => s.id === stepId);
  
  const isCompleted = stepIndex < currentStep;
  const isActive = stepIndex === currentStep;
  
  return (
    <span
      className={cn(
        'text-sm font-semibold transition-colors mt-2 text-center whitespace-nowrap hidden md:block',
        isActive || isCompleted
          ? 'text-blue-600 dark:text-blue-500'
          : 'text-gray-600 dark:text-gray-400',
        className
      )}
    >
      {children}
    </span>
  );
};

interface StepperSeparatorProps {
  stepId: string;
  className?: string;
}

export const StepperSeparator: React.FC<StepperSeparatorProps> = ({ stepId, className }) => {
  const { currentStep, steps } = useStepperContext();
  const stepIndex = steps.findIndex(s => s.id === stepId);
  
  const isCompleted = stepIndex < currentStep;
  
  return (
    <div
      className={cn(
        'flex-1 h-px transition-all duration-300 mx-2',
        isCompleted
          ? 'bg-blue-600'
          : 'bg-gray-300 dark:bg-gray-600',
        className
      )}
    />
  );
};

interface StepperContentProps {
  children: React.ReactNode;
  stepId: string;
  className?: string;
}

export const StepperContent: React.FC<StepperContentProps> = ({ children, stepId, className }) => {
  const { currentStep, steps } = useStepperContext();
  const stepIndex = steps.findIndex(s => s.id === stepId);
  
  if (stepIndex !== currentStep) {
    return null;
  }
  
  return (
    <div className={cn('mt-8', className)}>
      {children}
    </div>
  );
};
